/**
 * The role the header badge and the Account page show (#860).
 *
 * Both showed the token's legacy tier (`superuser` / `admin` / `operator` /
 * `viewer`). Since #847/#854 a customer's authority is granted per org by the
 * BFF (`organization_member_roles`), not as a realm role, so an org admin's
 * token carries no `admin` and the badge called them "viewer" while every
 * Admin-gated action worked. The BFF's /api/auth/me answers the role in the
 * acting org (`orgRole`) and the effective persona roles; show those.
 */
import { highestRole, isRole, type Role } from '@jeket-cloud/types/auth';

/** Display names of the persona roles, keyed by `Role` value. */
export const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  engineer: 'Engineer',
  'site-operator': 'Site Operator',
  'event-operator': 'Event Operator',
};

export interface RoleDisplay {
  /** A `Role` value, `superuser` or `member`; keys the badge colour. */
  key: string;
  label: string;
  /** Where the role comes from, for the tooltip. */
  title: string;
}

export interface RoleDisplayUser {
  /** Legacy token tier; only `superuser` still means something on its own. */
  role: string;
  /** Effective persona roles (the BFF's once it answers, the token's before). */
  roles: readonly string[];
  /** Role in the acting org from /api/auth/me; undefined until it answers. */
  orgRole?: string | null;
}

export function displayRole(user: RoleDisplayUser): RoleDisplay {
  // Jeket platform staff: the tier still gates platform-wide UI (org
  // switching across every org, site lockdown), and no org role outranks it.
  if (user.role === 'superuser') {
    return { key: 'superuser', label: 'Superuser', title: 'Jeket platform superuser' };
  }
  if (isRole(user.orgRole)) {
    return { key: user.orgRole, label: ROLE_LABELS[user.orgRole], title: 'Your role in this organization' };
  }
  // A realm-wide persona grant: Jeket staff, or a customer admin not yet
  // moved off the realm `admin` role (scripts/migrate-customer-admins.mjs).
  const realm: Role | null = highestRole(user.roles);
  if (realm) return { key: realm, label: ROLE_LABELS[realm], title: 'Your role across Jeket' };
  return { key: 'member', label: 'Member', title: 'Member of this organization, with no role in it' };
}
